import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { useMobile } from "src/hooks/useMobile";
import { IAuthForm } from "src/services/AuthService";
import { FormParser } from "../FormParser";
import { StartScreenView } from "../StartScreenView";
import { S_AUTH_CHANGE_VIEW } from "./AuthSignals";

const passkey = "3fb2afd0add9eaa323654b67d395fe41eb1151c69fd82ef767ce611b0b1f7ba8";

const getForm = async () => {
	const form = {
		login: "",
		password: "",
		$__password_type: "password",
		controls: {
			remember: false,
			forgot: msg("forgotPassword"),
			$__forgot_type: "href",
		},
		$__controls_gridColumns: "1fr max-content",
		$__controls_blockless: true,
		spacer: "10px",
		$__spacer_type: "spacer",
	};
	try {
		const lf = sessionStorage.getItem("credentials");
		if (lf) {
			const f = JSON.parse(lf);
			form.login = f.login;
			const pass = await decryptString(f.password, passkey);
			form.password = pass; //f.password;
		}
	} catch (e) {}
	return form;
};

export const AuthLogin = (props: { login?: string; pass?: string }) => {
	useLocale();
	const [formData, setFormData] = useState<IAuthForm>();
	const [busy, setBusy] = useState(false);
	const { isMobile, isTablet } = useMobile();

	useEffect(() => {
		getForm().then(form => {
			if (props && props.login && props.pass) {
				form.login = props.login;
				form.password = props.pass;
			}
			setFormData(form as any as IAuthForm);
		});
	}, [props]);

	return (
		<StartScreenView busy={busy}>
			<Box
				sx={{
					width: isMobile ? "100%" : undefined,
					overflow: isMobile ? "auto" : undefined,
					display: "flex",
					height: "100%",
					minHeight: isMobile ? undefined : "700px",
					borderStartStartRadius: isMobile ? "0" : "12px",
					borderEndStartRadius: isMobile ? "0" : "12px",
					borderEndEndRadius: isTablet ? "12px" : "0",
					borderStartEndRadius: isTablet ? "12px" : "0",
					backgroundColor: "rgba(255,255,255,1)",
				}}>
				<Box
					sx={{
						padding: isMobile ? "34px 26px" : "60px",
						minWidth: isMobile ? "0" : "500px",
						width: isMobile ? "100%" : "auto",
						display: "flex",
						margin: isMobile ? "auto" : undefined,
						maxWidth: isMobile ? "500px" : undefined,
						flexDirection: "column",
					}}>
					<Typo capitalize sx={{ fontSize: "26px", fontWeight: 400 }}>{msg("cabinetAccess")}<br />{msg("pleaseEnterYour")}<br />{msg("credentialsToSignIn")}</Typo>
					{/* <Typo>Use your credential to log in</Typo> */}
					<Spacer size="40px" />

					{formData && (
						<FormParser<IAuthForm>
							key={"authForm"}
							ignoreObserver={true}
							secondaryButton={null}
							fullSizeButtons={true}
							primaryButton={{ title: msg("logIn"), variant: "primary", sx: { flexGrow: "1" } }}
							onComponentClick={cmp => {
								if (cmp.name === "forgot") {
									S_AUTH_CHANGE_VIEW.invoke("forgot");
								}
							}}
							onSubmit={async (data: IAuthForm): Promise<any> => {
								setBusy(true);
								const response = await GD.REQ_AUTH_LOGIN.request(data);
								setBusy(false);
								if (response) {
									if ("twofa_required" in response && response.twofa_required) {
										S_AUTH_CHANGE_VIEW.invoke("otp");
										return { data };
									}

									if ("device_verify_required" in response && response.device_verify_required) {
										S_AUTH_CHANGE_VIEW.invoke("otp");
										return { data };
									}

									if ("code" in response && response.code === 1001 && response.temp_token) {
										S_AUTH_CHANGE_VIEW.invoke("changePassword");
										return { data };
									}

									return { data: data, error: response };
								}
								// todo - encrypt password
								const encrypted = await encryptString(data.password, passkey);
								if (data.controls.remember) sessionStorage.setItem("credentials", JSON.stringify({ login: data.login, password: encrypted }));
							}}
							onRequest={async (): Promise<any> => {
								return formData;
							}}
						/>
					)}

					<Box
						sx={{
							display: "flex",
							gap: "5px",
							alignItems: "center",
							margin: "25px 0",
						}}>
						<Box
							sx={{
								border: "1px solid rgba(0,0,0,0.14)",
								width: "100%",
							}}
						/>
						<TypoCaption italic>{msg("or")}</TypoCaption>
						<Box
							sx={{
								border: "1px solid rgba(0,0,0,0.14)",
								width: "100%",
							}}
						/>
					</Box>
					<Button
						sx={{
							width: "auto",
						}}
						variant="secondary"
						onClick={() => S_AUTH_CHANGE_VIEW.invoke("reg1")}>{msg("signUp")}</Button>
				</Box>
			</Box>
		</StartScreenView>
	);
};

// CRYPTO
export type EncryptedPayload = {
	s: string /** salt, base64 */;
	iv: string /** iv (nonce), base64 */;
	ct: string /** ciphertext, base64 */;
};

const te = new TextEncoder();
const td = new TextDecoder();

const toB64 = (buf: ArrayBuffer | Uint8Array): string => {
	const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
	let s = "";
	for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
	return btoa(s);
};

const fromB64 = (b64: string): Uint8Array => {
	const bin = atob(b64);
	const u8 = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
	return u8;
};

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
	const keyMaterial = await crypto.subtle.importKey("raw", te.encode(password), "PBKDF2", false, ["deriveKey"]);
	return crypto.subtle.deriveKey(
		{
			name: "PBKDF2",
			salt: salt as any,
			iterations: 150_000,
			hash: "SHA-256",
		},
		keyMaterial,
		{ name: "AES-GCM", length: 256 },
		false, // not extractable
		["encrypt", "decrypt"],
	);
}

/** ENCRYPT */
export async function encryptString(plaintext: string, password: string): Promise<EncryptedPayload> {
	const salt = crypto.getRandomValues(new Uint8Array(16));
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const key = await deriveKey(password, salt);

	const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, te.encode(plaintext));

	return { s: toB64(salt), iv: toB64(iv), ct: toB64(ct) };
}

/** DECRYPT */
export async function decryptString(payload: EncryptedPayload, password: string): Promise<string> {
	const salt = fromB64(payload.s);
	const iv = fromB64(payload.iv) as any;
	const key = await deriveKey(password, salt);
	const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, fromB64(payload.ct) as any);
	return td.decode(pt);
}
